import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Edit, Trash2, ToggleLeft, ToggleRight, Package, Wrench } from 'lucide-react';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/alerts';

import { itemService } from '@/services/itemService';
import { Item, ItemFilters, ITEM_TYPES } from '@/types/item';
import { useAuth } from '@/context/AuthContext';
import AddItemModal from '@/components/AddItemModal';

const Items: React.FC = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<ItemFilters>({
    search: '',
    type: '',
    active: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Queries
  const { data: itemsData, isLoading, error, refetch } = useQuery({
    queryKey: ['items', page, limit, filters],
    queryFn: () => itemService.getItems({
      page,
      limit,
      search: filters.search || undefined,
      type: filters.type || undefined,
      active: filters.active !== '' ? String(filters.active) : undefined
    }),
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds
  });

  // Debug: Log received data
  React.useEffect(() => {
    if (itemsData?.items && itemsData.items.length > 0) {
      const firstItem = itemsData.items[0];
      const formattedDate = formatDate(firstItem.created_at);
      console.log('📦 Items page: Received items data', {
        totalItems: itemsData.items.length,
        sampleItem: {
          id: firstItem.id,
          name: firstItem.name,
          created_at: firstItem.created_at,
          created_at_raw: JSON.stringify(firstItem.created_at),
          hasCreatedAt: !!firstItem.created_at,
          createdAtType: typeof firstItem.created_at,
          formattedDate: formattedDate
        }
      });
    }
  }, [itemsData]);

  // Mutations
  const deleteItemMutation = useMutation({
    mutationFn: (id: number) => itemService.deleteItem(id),
    onSuccess: () => {
      showSuccess('Artículo eliminado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al eliminar artículo');
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => itemService.toggleItemStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al cambiar estado del artículo');
    }
  });

  // Event handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  const handleFilterChange = (key: keyof ItemFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDeleteItem = (item: Item) => {
    showDeleteConfirm(
      `¿Está seguro de eliminar el artículo "${item.name}"?`,
      () => deleteItemMutation.mutate(item.id)
    );
  };

  const handleToggleStatus = (item: Item) => {
    showDeleteConfirm(
      `¿Está seguro de ${item.active ? 'desactivar' : 'activar'} el artículo "${item.name}"?`,
      () => toggleStatusMutation.mutate(item.id),
      item.active ? 'Desactivar' : 'Activar',
      item.active ? 'danger' : 'success'
    );
  };

  const formatDate = (dateInput: string | Date | null | undefined) => {
    if (!dateInput) return 'N/A';
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const getItemTypeIcon = (type: 'PRODUCTO' | 'SERVICIO') => {
    return type === 'PRODUCTO' ? <Package className="h-4 w-4" /> : <Wrench className="h-4 w-4" />;
  };

  const canManage = hasPermission(['manage_items']);
  const canCreate = hasPermission(['create_items', 'manage_items']);
  const canEdit = hasPermission(['edit_items', 'manage_items']);
  const canDelete = hasPermission(['delete_items', 'manage_items']);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Productos y Servicios</h1>
              <p className="mt-1 text-sm text-gray-600">
                Gestiona tu catálogo de productos y servicios
              </p>
            </div>
            {canCreate && (
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setShowAddModal(true);
                }}
                className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus className="h-5 w-5" />
                <span className="sm:inline">Nuevo Artículo</span>
              </button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, código, descripción..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="btn-secondary flex items-center gap-2 flex-1 sm:flex-initial justify-center"
              >
                <Filter className="h-5 w-5" />
                <span>Filtros</span>
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 sm:flex-initial"
              >
                Buscar
              </button>
            </div>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">
                    Tipo de Artículo
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Todos los tipos</option>
                    {ITEM_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">
                    Estado
                  </label>
                  <select
                    value={filters.active?.toString() || ''}
                    onChange={(e) => handleFilterChange('active', e.target.value === '' ? '' : e.target.value === 'true')}
                    className="form-select"
                  >
                    <option value="">Todos los estados</option>
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="form-label invisible">Acciones</label>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ search: '', type: '', active: '' });
                      setPage(1);
                    }}
                    className="btn-secondary w-full"
                  >
                    Limpiar Filtros
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando artículos...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              <p>Error al cargar los artículos</p>
              <button 
                onClick={() => refetch()} 
                className="btn-secondary mt-2"
              >
                Reintentar
              </button>
            </div>
          ) : !itemsData?.items.length ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No se encontraron artículos</p>
              {canCreate && (
                <button
                  onClick={() => {
                    setSelectedItem(null);
                    setShowAddModal(true);
                  }}
                  className="btn-primary mt-4"
                >
                  Crear primer artículo
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Items Table - Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Artículo</th>
                      <th className="table-header-cell">Tipo</th>
                      <th className="table-header-cell">Código</th>
                      <th className="table-header-cell">Precio Costo</th>
                      <th className="table-header-cell">Precio Venta</th>
                      <th className="table-header-cell">Impuesto</th>
                      <th className="table-header-cell">Estado</th>
                      <th className="table-header-cell">Fecha</th>
                      {canManage && (
                        <th className="table-header-cell text-right">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {itemsData.items.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.name}
                            </div>
                            {item.description && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            {getItemTypeIcon(item.type)}
                            <span className="text-sm text-gray-900">
                              {item.type === 'PRODUCTO' ? 'Producto' : 'Servicio'}
                            </span>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-mono text-gray-600">
                            {item.code || 'N/A'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">
                            {formatPrice(item.cost_price)}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">
                            {formatPrice(item.price)}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-gray-900">
                            {item.tax_rate}%
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={item.active ? 'badge-success' : 'badge-danger'}>
                            {item.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="table-cell text-gray-500">
                          {formatDate(item.created_at)}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              {canEdit && (
                                <button
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowAddModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  onClick={() => handleToggleStatus(item)}
                                  className={`p-1 ${item.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                                  title={item.active ? 'Desactivar' : 'Activar'}
                                >
                                  {item.active ? (
                                    <ToggleRight className="h-4 w-4" />
                                  ) : (
                                    <ToggleLeft className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  onClick={() => handleDeleteItem(item)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Items Cards - Mobile */}
              <div className="lg:hidden space-y-4 p-4">
                {itemsData.items.map((item) => (
                  <div key={item.id} className="card">
                    <div className="card-body">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {getItemTypeIcon(item.type)}
                            <h3 className="text-lg font-medium text-gray-900">
                              {item.name}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500">
                            {item.type === 'PRODUCTO' ? 'Producto' : 'Servicio'}
                          </p>
                        </div>
                        <span className={item.active ? 'badge-success' : 'badge-danger'}>
                          {item.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>

                      {item.description && (
                        <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      )}

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Código</p>
                          <p className="font-mono text-gray-900">{item.code || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Precio Costo</p>
                          <p className="font-medium text-gray-900">{formatPrice(item.cost_price)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Precio Venta</p>
                          <p className="font-medium text-gray-900">{formatPrice(item.price)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Impuesto</p>
                          <p className="font-medium text-gray-900">{item.tax_rate}%</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-gray-500">Fecha de creación</p>
                          <p className="font-medium text-gray-900">{formatDate(item.created_at)}</p>
                        </div>
                      </div>

                      {canManage && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                          {canEdit && (
                            <button
                              onClick={() => {
                                setSelectedItem(item);
                                setShowAddModal(true);
                              }}
                              className="btn-sm btn-outline flex items-center gap-2 flex-1"
                            >
                              <Edit className="h-4 w-4" />
                              Editar
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`btn-sm flex items-center gap-2 flex-1 ${
                                item.active 
                                  ? 'btn-warning' 
                                  : 'btn-success'
                              }`}
                            >
                              {item.active ? (
                                <>
                                  <ToggleRight className="h-4 w-4" />
                                  Desactivar
                                </>
                              ) : (
                                <>
                                  <ToggleLeft className="h-4 w-4" />
                                  Activar
                                </>
                              )}
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteItem(item)}
                              className="btn-sm btn-danger flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {itemsData.pagination.pages > 1 && (
                <div className="px-4 sm:px-6 py-3 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                      Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, itemsData.pagination.total)} de {itemsData.pagination.total} artículos
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Anterior</span>
                        <span className="sm:hidden">‹</span>
                      </button>
                      <span className="text-sm text-gray-700 px-2">
                        <span className="hidden sm:inline">Página </span>{page} / {itemsData.pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= itemsData.pagination.pages}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Siguiente</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Item Modal */}
        <AddItemModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem || undefined}
        />
      </div>
    </div>
  );
};

export default Items;